import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { Elements } from '@stripe/react-stripe-js';

import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { createSetupIntent } from '@/lib/api/billing';
import { getStripe } from '@/lib/stripe/client';

import { OnboardingShell } from './OnboardingShell';
import { Step5BillingForm } from './Step5BillingForm';
import { StepIndicator } from './StepIndicator';

/**
 * Step 5 of 5 — billing. Per ADR 0017: capture a card during a 7-day
 * free trial via Stripe Elements. On mount we fetch a SetupIntent;
 * once its client_secret is ready we render the Elements provider
 * around the card form (`Step5BillingForm`).
 *
 * No charge happens at this step — the card is captured for the
 * post-trial invoice on day 8. Pricing is £0.03 per shipment, billed
 * monthly, UK VAT added at invoice.
 */
export function Step5Billing() {
  const navigate = useNavigate();
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [loadError, setLoadError] = useState(false);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setLoadError(false);
    setClientSecret(null);
    void createSetupIntent().then((result) => {
      if (cancelled) return;
      if (result.isErr()) {
        setLoadError(true);
        return;
      }
      setClientSecret(result.value.client_secret);
    });
    return () => {
      cancelled = true;
    };
  }, [attempt]);

  return (
    <OnboardingShell>
      <StepIndicator currentStep={5} />
      <Card>
        <CardHeader>
          <CardTitle>Start your 7-day free trial</CardTitle>
          <CardDescription>
            No charges during the trial. Cancel anytime from settings before day 8.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="border-border bg-muted/40 space-y-1 rounded-md border p-4">
            <p className="text-foreground text-sm font-medium">Pay-as-you-go pricing</p>
            <p className="text-muted-foreground text-sm">
              <span className="text-foreground tabular-nums">£0.03</span> per shipment, billed
              monthly. UK VAT added at invoice.
            </p>
          </div>

          {loadError ? (
            <div className="space-y-3">
              <Alert variant="destructive">
                <AlertDescription>
                  We couldn&apos;t start the payment form. Please try again.
                </AlertDescription>
              </Alert>
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setAttempt((n) => n + 1);
                }}
              >
                Retry
              </Button>
            </div>
          ) : clientSecret === null ? (
            <div className="space-y-3" data-testid="billing-loading">
              <div className="bg-muted h-10 w-full animate-pulse rounded-md" />
              <div className="bg-muted h-10 w-full animate-pulse rounded-md" />
            </div>
          ) : (
            <Elements stripe={getStripe()} options={{ clientSecret }}>
              <Step5BillingForm />
            </Elements>
          )}

          {loadError ? (
            <div className="flex justify-start">
              <Button
                type="button"
                variant="ghost"
                onClick={() => {
                  void navigate('/app/onboarding/step/4');
                }}
              >
                Back
              </Button>
            </div>
          ) : null}
        </CardContent>
      </Card>
    </OnboardingShell>
  );
}
